import { z } from 'zod';
import { RecurrenceRuleSchema } from './events.js';
/**
 * Campaign — outbound messaging (auto-dialer + AI-voice + SMS + email).
 * Distinct from Event: a campaign is a one-shot or recurring message, not a
 * reminder for an upcoming appointment.
 */
export const CampaignChannelSchema = z.enum(['auto_dialer', 'ai_message', 'sms', 'email']);
export const CampaignStatusSchema = z.enum(['pending', 'in_progress', 'completed', 'failed']);
const ChannelDeliveryStatusSchema = z.enum([
    'na',
    'pending',
    'sent',
    'completed',
    'failed',
    'caller_disconnect',
]);
export const CampaignDeliveryStatusSchema = z.object({
    auto_dialer: ChannelDeliveryStatusSchema.optional(),
    ai_message: ChannelDeliveryStatusSchema.optional(),
    sms: ChannelDeliveryStatusSchema.optional(),
    email: ChannelDeliveryStatusSchema.optional(),
});
/**
 * Auto-dialer "Assigned to" target. When set on an `auto_dialer` campaign
 * the scheduler dispatches the call in BRIDGE mode (ring this internal
 * extension or call-queue first, then live-bridge to the external party
 * with no TTS). When null/undefined the campaign runs in DIRECT mode and
 * the external party hears the configured TTS message.
 */
export const CampaignAssigneeKindSchema = z.enum(['extension', 'callgroup']);
export const CampaignSchema = z.object({
    id: z.string().regex(/^cmp_[A-Za-z0-9_-]+$/),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    contact_profile_id: z.string().regex(/^cp_[A-Za-z0-9_-]+$/).nullable().optional(),
    message_template_id: z.string().regex(/^tpl_[A-Za-z0-9_-]+$/).nullable().optional(),
    /** Display label. */
    tag: z.string().min(1).max(120),
    recipient_name: z.string().min(1).max(120).optional(),
    recipient_email: z.string().email().optional(),
    /** E.164 format. */
    recipient_phone: z.string().min(1).max(32),
    start_at: z.string().datetime(),
    recurrence: RecurrenceRuleSchema.nullable().optional(),
    channels: z.array(CampaignChannelSchema).min(1),
    status: CampaignStatusSchema.default('pending'),
    delivery_status: CampaignDeliveryStatusSchema.default({}),
    parent_campaign_id: z.string().regex(/^cmp_[A-Za-z0-9_-]+$/).nullable().optional(),
    dnc: z.boolean().default(false),
    assignee_kind: CampaignAssigneeKindSchema.nullable().optional(),
    assignee_value: z.string().min(1).max(64).nullable().optional(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CampaignCreateSchema = z.object({
    contact_profile_id: z.string().regex(/^cp_[A-Za-z0-9_-]+$/).optional(),
    message_template_id: z.string().regex(/^tpl_[A-Za-z0-9_-]+$/).optional(),
    tag: z.string().min(1).max(120),
    recipient_name: z.string().min(1).max(120).optional(),
    recipient_email: z.string().email().optional(),
    recipient_phone: z.string().min(1).max(32),
    start_at: z.string().datetime(),
    recurrence: RecurrenceRuleSchema.optional(),
    channels: z.array(CampaignChannelSchema).min(1),
    assignee_kind: CampaignAssigneeKindSchema.optional(),
    assignee_value: z.string().min(1).max(64).optional(),
});
export const CampaignUpdateSchema = z
    .object({
    tag: z.string().min(1).max(120).optional(),
    start_at: z.string().datetime().optional(),
    channels: z.array(CampaignChannelSchema).min(1).optional(),
    message_template_id: z.string().regex(/^tpl_[A-Za-z0-9_-]+$/).optional(),
    /** Pass null to clear. */
    assignee_kind: CampaignAssigneeKindSchema.nullable().optional(),
    assignee_value: z.string().min(1).max(64).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'at least one field required' });
//# sourceMappingURL=campaigns.js.map